import React, { useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  Warning,
  Info,
  FileUpload
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { API_ENDPOINTS } from '../config';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.default,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
  },
}));

// Types
interface CorrectionResult {
  total_rows: number;
  corrections_found: number;
  corrections_applied: number;
  skipped_rows: number;
  already_exists: number;
  errors: string[];
  correction_details: Array<{
    prediction_id: string;
    original_status: string;
    original_data_status: string;
    corrected_status: string;
    corrected_data_status: string;
    row_number: number;
  }>;
}

interface CorrectionsProps {
  open?: boolean;
  onClose?: () => void;
  embedded?: boolean;
}

export const Corrections: React.FC<CorrectionsProps> = ({ embedded = false }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<CorrectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setError(null);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(API_ENDPOINTS.UPLOAD_CORRECTIONS, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const correctionResult = await response.json();
        setResult(correctionResult);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to process corrections file');
      }
    } catch (err) {
      setError('Network error occurred while uploading corrections');
    } finally {
      setUploading(false);
    }
  };

  const getSeverityIcon = (type: 'success' | 'warning' | 'error' | 'info') => {
    switch (type) {
      case 'success': return <CheckCircle color="success" />;
      case 'warning': return <Warning color="warning" />;
      case 'error': return <Error color="error" />;
      case 'info': return <Info color="info" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        🔧 Prediction Corrections
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload an Excel file with corrected prediction values to update the model's training data.
        Supports both traditional format (full columns) and enhanced format (with Processing_Type).
      </Typography>

      {/* Upload Section */}
      {!result && (
        <UploadBox sx={{ mb: 3 }}>
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Upload Corrections File
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an Excel file (.xlsx, .xls) with your corrections.
            Compatible with both traditional and enhanced CSV output formats.
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              disabled={uploading}
            >
              Choose File
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                ref={fileInputRef}
              />
            </Button>
            
            {file && (
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <FileUpload />}
              >
                {uploading ? 'Processing...' : 'Upload Corrections'}
              </Button>
            )}
          </Stack>

          {file && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected file: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </UploadBox>
      )}

      {/* Error Display */}
      {error && !result && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Display */}
      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              📊 Correction Results
            </Typography>
            
            {/* Summary Stats */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Box>
                  <Typography variant="h4" color="primary">
                    {result.total_rows}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Rows
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {result.corrections_found}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Corrections Found
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="success.main">
                    {result.corrections_applied}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Applied Successfully
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="info.main">
                    {result.already_exists}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Already Exists
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="text.secondary">
                    {result.skipped_rows}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Skipped
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Status Alerts */}
            {result.corrections_applied > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully applied {result.corrections_applied} corrections to the training data!
              </Alert>
            )}

            {result.already_exists > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {result.already_exists} correction(s) already exist in the system with the same values and were skipped.
              </Alert>
            )}

            {result.errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {result.errors.length} error(s) occurred during processing.
              </Alert>
            )}

            {/* Correction Details Table */}
            {result.correction_details.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Applied Corrections
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row #</TableCell>
                        <TableCell>Prediction ID</TableCell>
                        <TableCell>Original Status</TableCell>
                        <TableCell>Original Data Status</TableCell>
                        <TableCell>Corrected Status</TableCell>
                        <TableCell>Corrected Data Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.correction_details.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell>{detail.row_number}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {detail.prediction_id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={detail.original_status} size="small" color="error" />
                          </TableCell>
                          <TableCell>
                            <Chip label={detail.original_data_status} size="small" color="error" />
                          </TableCell>
                          <TableCell>
                            <Chip label={detail.corrected_status} size="small" color="success" />
                          </TableCell>
                          <TableCell>
                            <Chip label={detail.corrected_data_status} size="small" color="success" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {result.correction_details.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Showing all {result.correction_details.length} corrections that were applied
                  </Typography>
                )}
              </Box>
            )}

            {/* Error Details */}
            {result.errors.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom color="error">
                  Errors
                </Typography>
                <List dense>
                  {result.errors.slice(0, 5).map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Error color="error" />
                      </ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
                {result.errors.length > 5 && (
                  <Typography variant="body2" color="text.secondary">
                    Showing first 5 of {result.errors.length} errors
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Another File Button */}
      {result && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={resetForm}
            startIcon={<CloudUpload />}
            size="large"
          >
            Upload Another File
          </Button>
        </Box>
      )}
    </Box>
  );
}; 